'use client';
import React, { useState, useMemo, useEffect } from 'react';
import { mockAccounts as initialMockAccounts } from '@/data/mockAccounts';
import SectionHeading from '@/components/ui/SectionHeading';
import ListingCard from '@/components/ui/ListingCard';
import FlagIcon from '@/components/ui/FlagIcon';
import { getAccounts, triggerScrape } from '@/lib/bridge';

import EmptyStateCard from './EmptyStateCard';

const ALL_REGIONS = 'Tất cả';
const ALL_STATUSES = 'all';
const PAGE_SIZE = 20;

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: ALL_STATUSES, label: 'Tất cả' },
  { value: 'normal', label: 'Bình thường' },
  { value: 'checking', label: 'Đang kiểm tra' },
  { value: 'warning', label: 'Cảnh báo' },
];

export default function ListingSection() {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState(ALL_REGIONS);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [currentPage, setCurrentPage] = useState(1);
  const [scraping, setScraping] = useState(false);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAccounts = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAccounts();
        
        const regionMap: Record<string, { label: string, code: string }> = {
          '美': { label: 'US', code: 'US' },
          '港': { label: 'HK', code: 'HK' },
          '日': { label: 'JP', code: 'JP' },
          '台': { label: 'TW', code: 'TW' },
          '新': { label: 'SG', code: 'SG' },
          '韩': { label: 'KR', code: 'KR' },
          '英': { label: 'GB', code: 'GB' },
          '越': { label: 'VN', code: 'VN' },
          '泰国': { label: 'TH', code: 'TH' },
          '泰': { label: 'TH', code: 'TH' },
          '菲': { label: 'PH', code: 'PH' },
          '马': { label: 'MY', code: 'MY' },
          '印': { label: 'ID', code: 'ID' },
          '俄': { label: 'RU', code: 'RU' },
          '澳': { label: 'AU', code: 'AU' },
          '土': { label: 'TR', code: 'TR' },
          '中': { label: 'CN', code: 'CN' },
        };

        const getRegionInfo = (tag: string) => {
          if (!tag) return { label: 'VN', code: 'VN' };
          for (const key in regionMap) {
            if (tag.includes(key)) return regionMap[key];
          }
          const clean = tag.replace(/[【】区ID]/g, '').trim();
          return { label: clean || 'VN', code: 'VN' };
        };

        const adapted = data.map((acc: any) => {
          const info = getRegionInfo(acc.regionTag);
          const normalizedStatus = String(acc.status || '').toLowerCase();
          const checkedAt = acc.checkTime || (acc.scrapedAt ? new Date(acc.scrapedAt).toISOString() : new Date().toISOString());

          return {
            id: acc.id,
            maskedAccount: acc.maskedEmail || '********',
            region: info.label,
            regionCode: info.code,
            status: normalizedStatus === 'normal' ? 'normal' : 'warning',
            checkedAt,
            sourceLabel: 'Shadowrocket',
            description: 'Tài khoản Apple ID dùng chung',
            mockAccountValue: acc.fullEmail,
            mockPasswordValue: acc.password,
            tab: 1,
            scrapedAt: Number(acc.scrapedAt || 0),
          };
        });
        adapted.sort((a: any, b: any) => {
          const aTime = Date.parse(a.checkedAt);
          const bTime = Date.parse(b.checkedAt);
          if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) return bTime - aTime;
          return (b.scrapedAt || 0) - (a.scrapedAt || 0);
        });
        setAccounts(adapted);
      } catch (e: any) {
        console.error('Failed to fetch accounts:', e);
        setError(e.message || 'Lỗi kết nối tới Bridge Server');
        // Clean mock data if it hits fallback
        const cleanedMock = initialMockAccounts.map(acc => ({
          ...acc,
          sourceLabel: 'Shadowrocket',
          description: 'Hỗ trợ tải Shadowrocket miễn phí'
        }));
        setAccounts(cleanedMock);
      } finally {
        setLoading(false);
      }
    };
    loadAccounts();
  }, []);

  const allRegions = useMemo(() => {
    const unique = accounts.reduce((acc: any[], curr: any) => {
      if (!acc.find(item => item.label === curr.region)) {
        acc.push({ label: curr.region, code: curr.regionCode });
      }
      return acc;
    }, []);
    return [{ label: ALL_REGIONS, code: '' }, ...unique];
  }, [accounts]);

  const filtered = useMemo(() => {
    return accounts.filter((a: any) => {
      const regionMatch = regionFilter === ALL_REGIONS || a.region === regionFilter;
      const statusMatch = statusFilter === ALL_STATUSES || a.status === statusFilter;
      return regionMatch && statusMatch;
    });
  }, [regionFilter, statusFilter, accounts]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filtered]);

  const normalCount = accounts.filter(a => a.status === 'normal').length;

  const isFiltered = regionFilter !== ALL_REGIONS || statusFilter !== ALL_STATUSES;

  // Build page number list with ellipsis
  const getPageNumbers = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages: (number | '...')[] = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }
    return pages;
  };

  return (
    <section id="listing" className="py-8 sm:py-12" aria-labelledby="listing-heading">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <SectionHeading
            eyebrow="Apple ID chia sẻ"
            title="Danh sách ID hoạt động"
            subtitle="Chọn ID có trạng thái Bình thường, copy và dùng trong App Store."
          />
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2 text-sm text-muted">
              <span className={`w-2 h-2 rounded-full ${loading ? 'bg-orange-400' : error ? 'bg-red-500' : 'bg-green-500 animate-pulse'}`} aria-hidden="true" />
              <span>{loading ? 'Đang tải dữ liệu...' : error ? `Lỗi: ${error}` : `${normalCount} ID đang hoạt động`}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap scrollbar-thin">
            {allRegions.map(region => (
              <button
                key={region.label}
                onClick={() => setRegionFilter(region.label)}
                className={`px-3 py-1.5 rounded-pill text-xs font-medium border transition-all min-h-[36px] whitespace-nowrap flex-shrink-0 flex items-center gap-2 ${
                  regionFilter === region.label
                    ? 'bg-primary text-white border-primary' :'bg-white text-muted border-border hover:border-primary/40 hover:text-foreground'
                }`}
                aria-pressed={regionFilter === region.label}
              >
                {region.code ? (
                  <FlagIcon countryCode={region.code} size={14} />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                )}
                {region.label}
              </button>
            ))}
          </div>

          <div className="flex gap-1.5">
            {STATUS_FILTERS.map(sf => (
              <button
                key={sf.value}
                onClick={() => setStatusFilter(sf.value)}
                className={`px-3 py-1.5 rounded-pill text-xs font-medium border transition-all min-h-[36px] flex-1 sm:flex-none ${
                  statusFilter === sf.value
                    ? 'bg-accent text-white border-accent' :'bg-white text-muted border-border hover:border-accent/40 hover:text-foreground'
                }`}
                aria-pressed={statusFilter === sf.value}
              >
                {sf.label}
              </button>
            ))}
          </div>
        </div>

        {/* Listing */}
        <div id="tab-panel-all" role="region" aria-label="Danh sách Apple ID">
          {loading ? (
            <div className="py-20 text-center animate-pulse text-muted">Đang truy vấn Bridge Server...</div>
          ) : error ? (
            <div className="py-20 text-center">
              <div className="text-red-500 font-bold mb-2">Kết nối thất bại</div>
              <div className="text-muted text-sm mb-4">{error}</div>
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold"
              >
                Thử lại
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyStateCard
              onSwitchTab={() => {
                setRegionFilter(ALL_REGIONS);
                setStatusFilter(ALL_STATUSES);
              }}
              isFiltered={isFiltered}
            />
          ) : (
            <div className="flex flex-col gap-2.5">
              {paginated.map(record => (
                <ListingCard key={record.id} record={record} />
              ))}
            </div>
          )}
        </div>

        {/* Footer note */}
        {filtered.length > 0 && (
          <p className="mt-4 text-xs text-muted text-center">
            Hiển thị {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} / {filtered.length} tài khoản · Cập nhật tự động mỗi ngày · Kiểm tra trạng thái trước khi dùng
          </p>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-5 flex items-center justify-center gap-1 flex-wrap" aria-label="Phân trang">
            {/* Prev */}
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-sm font-medium text-muted transition-all hover:border-primary/40 hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Trang trước"
            >
              ‹
            </button>

            {/* Page numbers */}
            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="w-9 h-9 flex items-center justify-center text-sm text-muted select-none">
                  …
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page as number)}
                  className={`w-9 h-9 rounded-lg border text-sm font-medium transition-all ${
                    currentPage === page
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'border-border text-muted hover:border-primary/40 hover:text-primary'
                  }`}
                  aria-current={currentPage === page ? 'page' : undefined}
                  aria-label={`Trang ${page}`}
                >
                  {page}
                </button>
              )
            )}

            {/* Next */}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-border text-sm font-medium text-muted transition-all hover:border-primary/40 hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Trang sau"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
